import React, { useCallback, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { SiteRagReport } from '../../../types/siteRag';
import type { UserAuditResult } from '../../../types/userAudit';
import type { CitationSupportFinding } from '../../../lib/audit/citationSemanticVerifier';
import { buildCitationSupportFixBlock } from '../../../lib/audit/citationSemanticVerifier';
import { Button } from '../../ui/button';
import { useStore } from '../../../store';

type CitationSupportSnapshot = {
  findings?: CitationSupportFinding[];
  pairsScanned?: number;
  llmCalls?: number;
  skippedLowCitationCount?: boolean;
};

type QaFindingsWithCitationSupport = NonNullable<SiteRagReport['qaFindings']> & {
  citationSupport?: CitationSupportSnapshot;
};

type CitationSupportMeta = {
  pairs: number;
  llmCalls: number;
  skipped: boolean;
};

function severityClass(severity: CitationSupportFinding['severity']): string {
  return severity === 'block'
    ? 'border-danger/40 bg-danger/10 text-text-primary'
    : 'border-warning/40 bg-warning/10 text-text-primary';
}

export function CitationSupportPanel({
  siteId,
  reports,
  auditResult,
}: {
  siteId: string;
  reports: SiteRagReport[];
  auditResult?: UserAuditResult | null;
}) {
  const generateTypedReport = useStore((s) => s.generateTypedReport);
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);

  const rows = useMemo(() => {
    const out: Array<{ report: SiteRagReport; finding: CitationSupportFinding }> = [];

    if (auditResult?.citationSupportByReport) {
      for (const report of reports) {
        const snapshot = auditResult.citationSupportByReport[report.id];
        for (const f of snapshot?.findings ?? []) {
          out.push({ report, finding: f });
        }
      }
    }

    for (const report of reports) {
      const qaFindings = report.qaFindings as QaFindingsWithCitationSupport | undefined;
      for (const f of qaFindings?.citationSupport?.findings ?? []) {
        if (out.some((r) => r.report.id === report.id && r.finding.pairId === f.pairId)) continue;
        out.push({ report, finding: f });
      }
    }

    return out;
  }, [auditResult?.citationSupportByReport, reports]);

  const meta = useMemo(() => {
    if (auditResult?.citationSupportByReport) {
      return Object.values(auditResult.citationSupportByReport).reduce<CitationSupportMeta>(
        (acc, snap) => ({
          pairs: acc.pairs + snap.pairsScanned,
          llmCalls: acc.llmCalls + snap.llmCalls,
          skipped: acc.skipped || snap.skippedLowCitationCount,
        }),
        { pairs: 0, llmCalls: 0, skipped: false },
      );
    }
    const fromReports = reports
      .map((r) => (r.qaFindings as QaFindingsWithCitationSupport | undefined)?.citationSupport)
      .filter((snap): snap is CitationSupportSnapshot => Boolean(snap));
    return fromReports.reduce<CitationSupportMeta>(
      (acc, snap) => ({
        pairs: acc.pairs + (snap?.pairsScanned ?? 0),
        llmCalls: acc.llmCalls + (snap?.llmCalls ?? 0),
        skipped: acc.skipped || (snap?.skippedLowCitationCount ?? false),
      }),
      { pairs: 0, llmCalls: 0, skipped: false },
    );
  }, [auditResult?.citationSupportByReport, reports]);

  const findingsByReport = useMemo(() => {
    const map = new Map<string, CitationSupportFinding[]>();
    for (const { report, finding } of rows) {
      const list = map.get(report.id) ?? [];
      list.push(finding);
      map.set(report.id, list);
    }
    return map;
  }, [rows]);

  const handleRegenerate = useCallback(async (report: SiteRagReport) => {
    const reportFindings = findingsByReport.get(report.id) ?? [];
    if (reportFindings.length === 0 || !report.reportType) return;
    const fixBlock = buildCitationSupportFixBlock(reportFindings);
    const siteConfigOverrides = {
      citationSupportRegenerateFocus: fixBlock,
    } as unknown as Parameters<typeof generateTypedReport>[8];
    setRegeneratingId(report.id);
    try {
      await generateTypedReport(
        siteId,
        report.reportType as import('../../../config/reportTypes').ReportTypeId,
        report.typedPreferences,
        'replace',
        report.id,
        undefined,
        undefined,
        undefined,
        siteConfigOverrides,
      );
    } finally {
      setRegeneratingId(null);
    }
  }, [findingsByReport, generateTypedReport, siteId]);

  if (rows.length === 0 && meta.pairs === 0 && !meta.skipped) return null;

  return (
    <section className="citation-support-panel mt-4" data-testid="citation-support-panel">
      <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary mb-2">
        Citation Support (R-15)
      </h3>
      <p className="text-sm text-text-secondary mb-3">
        Embedding pre-filter plus Gemini Flash judge verifies cited sources support surrounding claims.
        Opt-in via <code>VITE_AUDIT_ENABLE_CITATION_SUPPORT_VERIFICATION</code>.
      </p>
      {meta.skipped && (
        <p className="text-xs text-text-secondary mb-3">
          Skipped — fewer than 5 distinct inline citations in report body.
        </p>
      )}
      {meta.pairs > 0 && (
        <p className="text-xs text-text-secondary mb-3">
          Scanned {meta.pairs} claim/citation pair(s) · {meta.llmCalls} Flash judge call(s)
        </p>
      )}
      {rows.length === 0 ? (
        <p className="text-sm text-text-secondary">No unsupported or contradicted citation pairs.</p>
      ) : (
        <ul className="space-y-3 text-sm">
          {rows.slice(0, 25).map(({ report, finding: f }) => (
            <li
              key={`${report.id}:${f.pairId}`}
              className={`rounded border px-3 py-2 ${severityClass(f.severity)}`}
              data-testid={`citation-support-finding-${f.pairId}`}
            >
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="font-medium">{report.reportType ?? report.id}</span>
                <span className="text-xs uppercase">{f.severity}</span>
                <span className="text-xs uppercase">{f.verdict}</span>
                <span className="text-xs font-mono">{f.sectionId} · [Ref {f.refIndex}]</span>
                <span className="text-xs text-text-secondary">via {f.route}</span>
              </div>
              <p className="text-xs italic mb-2">&ldquo;{f.claimText}&rdquo;</p>
              <p className="text-xs">{f.rationale}</p>
              {f.similarity != null && (
                <p className="text-xs mt-1 text-text-secondary">
                  Embedding similarity: {f.similarity.toFixed(2)}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      {rows.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {[...findingsByReport.entries()].map(([reportId, reportFindings]) => {
            const report = reports.find((r) => r.id === reportId);
            if (!report) return null;
            return (
              <Button
                key={reportId}
                type="button"
                size="sm"
                variant="outline"
                disabled={regeneratingId === reportId}
                onClick={() => handleRegenerate(report)}
                data-testid={`citation-support-regenerate-${reportId}`}
              >
                {regeneratingId === reportId && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                Regenerate {report.reportType ?? 'report'} with fix hints
              </Button>
            );
          })}
        </div>
      )}
    </section>
  );
}
